import { array, fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import $ from 'jquery';

import type { Service } from 'webapp-types';
import { TemplateObjectType } from 'webapp/enums/enums';
import { redirect } from 'webapp/utils/common';

import joinClasses from 'webapp/helpers/join-classes';
import didInsert from 'webapp/modifiers/did-insert';
import BillingLimitationWarningComponent from 'webapp/components/billing/limitation-warning';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import VerificationBadgeComponent from 'webapp/components/verification/badge';

import Report from 'webapp/models-custom/report';
import Template from 'webapp/models-custom/template';

import { Defaults as AccountDefaultsData } from 'webapp/services/account';

interface Args {
	model: Template | Report;
	onEdit: () => void;
	/** Size of the Edit and Present buttons. The template CTA always stays normal. */
	buttonSize?: 'small' | 'normal';
	isEditBtnShown?: boolean;
	isShownInPopup?: boolean;
	onUse?: (model: Template) => void;
	present?: (isFullscreen?: boolean) => void;
}

export default class CommonRenderingPreviewHeaderComponent extends Component<Args> {
	@service('account') declare accountService: Service.Account;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('request') declare requestService: Service.Request;

	get isTemplate() {
		return this.args.model instanceof Template;
	}

	get verificationInfo() {
		const model = this.args.model;
		return model instanceof Template ? undefined : model.verificationInfo;
	}

	get isBoardTemplate() {
		return this.modelAsTemplate?.objectType === TemplateObjectType.BOARD;
	}

	get modelAsTemplate() {
		return this.isTemplate ? (this.args.model as Template) : null;
	}

	get isPublicTemplate() {
		return !!this.modelAsTemplate?.isPublic;
	}

	get isReportTemplate() {
		return this.modelAsTemplate?.objectType === TemplateObjectType.REPORT;
	}

	get isShownInDatawallButNotPublic() {
		return this.navigationService.isDatawallApp && !this.isPublicTemplate;
	}

	get isShownInPopupOrShownInDatawallButNotPublic() {
		return this.args.isShownInPopup || this.isShownInDatawallButNotPublic;
	}

	get logoSrc(): string {
		return this.isShownInPopupOrShownInDatawallButNotPublic
			? this.accountService.shownCompanyLogo
			: AccountDefaultsData.APP_LOGO_BIG_DARK;
	}

	get logoAlt(): string {
		return this.accountService.shownCompanyName;
	}

	get domain(): string {
		return this.args.isShownInPopup
			? this.accountService.domain
			: this.isShownInDatawallButNotPublic
				? ''
				: AccountDefaultsData.DOMAIN;
	}

	get isEditBtnShown() {
		return this.args.isEditBtnShown && !this.isPublicTemplate;
	}

	get ctaText(): string {
		// Called for templates only
		const ctaText = this.isBoardTemplate
			? 'Try this dashboard for free'
			: 'Connect & personalize';

		return this.isShownInPopupOrShownInDatawallButNotPublic ? 'Use template' : ctaText;
	}

	get isCtaBtnHidden(): boolean {
		return !this.isTemplate || (!this.args.isShownInPopup && !this.isPublicTemplate);
	}

	get showLimitationWarning(): boolean {
		return (
			this.isReportTemplate &&
			!this.mmReportsService.checkIfCanUseTemplate(this.isPublicTemplate)
		);
	}

	get logoUrl(): string {
		const domain = this.domain,
			url = this.accountService.whiteLabelingEnabled
				? domain
					? `https://${domain}`
					: '/'
				: 'https://databox.com';
		return url;
	}

	get showReportPresent() {
		return !this.isTemplate && !!this.args.present;
	}

	get isPageless() {
		return this.args.model instanceof Report && this.args.model.isPageless;
	}

	get buttonSizeClass(): string {
		return `size-${this.args.buttonSize ?? 'normal'}`;
	}

	getShareUrl(service: string) {
		const model = this.args.model,
			url = `${this.domain}/datawall/${model.token}`,
			text = `Check out this great free ${model.title} ${this.isReportTemplate ? 'report' : 'dashboard'} `;

		switch (service) {
			case 'twitter':
				return `https://twitter.com/intent/tweet?text=${text}&url=${url}`;
			case 'facebook':
				return `https://www.facebook.com/sharer/sharer.php?u=${url}`;
			case 'linkedin':
				return `https://www.linkedin.com/shareArticle?url=${url}&amp;title=${model.title} dashboard&amp;summary=${text}&amp;mini=true`;
			default:
				return '';
		}
	}

	@action
	share(service: string): void {
		const url = this.getShareUrl(service);

		if (url) {
			window.open(
				url,
				'_blank',
				'height=250,width=450,modal=yes,alwaysRaised=yes,toolbar=0,location=0,menubar=0',
			);
		}
	}

	@action
	use(): void {
		// Called for templates only
		const model = this.modelAsTemplate;
		if (!model?.id) {
			return;
		}

		const isTemplateAlreadyGenerating = this.infolayerService.getLayerById(model.id);
		if (isTemplateAlreadyGenerating) {
			return;
		}

		let useSpecified = false;
		if (this.args.onUse) {
			this.args.onUse(model);
			useSpecified = true;
		}

		if (!useSpecified) {
			// if no special action specified, execute defaults
			if (this.currentUserService.isLoggedIn) {
				// if user is logged in, go to template preview + connect
				redirect(
					`/${this.isReportTemplate ? 'reports' : 'databoards'}/public-templates/${model.id}/connect`,
				);
			} else {
				// if not logged in, go to signup with templateId
				const refererUrl = encodeURIComponent(window.location.href);
				redirect(
					`${this.requestService.websiteUrl}/signup?templateId=${model.id}&refererUrl=${refererUrl}`,
				);
			}
		}
	}

	@action
	edit() {
		this.args.onEdit();
	}

	@action
	present() {
		this.callPresentFn(true);
	}

	@action
	presentWithNotes() {
		this.callPresentFn();
	}

	private callPresentFn(isFullscreen = false) {
		const presentFn = this.args.present;
		if (presentFn) {
			presentFn(isFullscreen);
		}
	}

	@action
	setupView() {
		$(window).trigger('resize.datawallcenter');
	}
	<template>
		<div
			class={{joinClasses
				(array "preview-header" (if this.isPublicTemplate "" "can-have-actions"))
			}}
			{{didInsert this.setupView}}
		>

			{{#unless @isShownInPopup}}
				<a class="logo-wrapper" href={{this.logoUrl}}>
					<img class="logo" src={{this.logoSrc}} alt={{this.logoAlt}} />
				</a>
			{{/unless}}

			<div class="break"></div>

			<h1>{{@model.title}}</h1>
			<VerificationBadgeComponent
				@verification={{this.verificationInfo}}
				@entityLabel="report"
				@size="sm"
			/>

			{{#if this.isPublicTemplate}}
				<span class="by">
					<small>by</small>
					{{{this.modelAsTemplate.createdByText}}}
				</span>
				<div class="share">
					<span class="label">Share</span>
					<a role="button" class="social-icon" {{on "click" (fn this.share "twitter")}}>
						<CommonUiIconComponent
							@name="BrandX"
							@size="sm"
							@stroke="currentColor"
							@fill="none"
						/>
					</a>
					<a role="button" class="social-icon" {{on "click" (fn this.share "facebook")}}>
						<CommonUiIconComponent
							@name="BrandFacebook"
							@size="sm"
							@stroke="currentColor"
							@fill="none"
						/>
					</a>
					<a role="button" class="social-icon" {{on "click" (fn this.share "linkedin")}}>
						<CommonUiIconComponent
							@name="BrandLinkedin"
							@size="sm"
							@stroke="currentColor"
							@fill="none"
						/>
					</a>
				</div>
			{{/if}}

			<div class="actions-right">

				{{#if this.isEditBtnShown}}
					<a
						role="button"
						class="button theme-grey-outlined edit {{this.buttonSizeClass}}"
						{{on "click" this.edit}}
					>
						<CommonUiIconComponent @name="Pencil" @size="sm" />
						<span>Edit</span>
					</a>
				{{/if}}

				{{#if this.showReportPresent}}
					<a class="button present {{this.buttonSizeClass}}" {{on "click" this.present}}>
						<CommonUiIconComponent @name="Play" @size="sm" />
						Present
					</a>
					{{#unless this.isPageless}}
						<a
							class="button present theme-blue-outlined {{this.buttonSizeClass}}"
							{{on "click" this.presentWithNotes}}
						>
							<CommonUiIconComponent @name="PresentNotes" @size="sm" />
							Present with notes
						</a>
					{{/unless}}
				{{/if}}

				{{#unless this.isCtaBtnHidden}}
					<a role="button" class="button size-normal" {{on "click" this.use}}>
						{{this.ctaText}}
						{{#if this.showLimitationWarning}}
							<BillingLimitationWarningComponent />
						{{/if}}
					</a>
				{{/unless}}
			</div>
		</div>
	</template>
}
